// ====================================================================================================
// InviteTableSection.jsx
// ----------------------------------------------------------------------------------------------------
// Carrier-side bid matrix used by CarrierSubmitPage.
//
// Main responsibilities:
// - Render one editable row per Tender lane.
// - Support create, edit and view modes.
// - Validate Bid Value, Loads and Estimated Transit Days before submission.
// - Enforce remaining lane capacity based on accepted loads.
// - Support Excel export/import for carrier quote entry.
// - Support table toolbar actions, advanced filters, saved views, column visibility and pagination.
// - Render Bid Value with an optional currency suffix, such as "$" or "€".
// ====================================================================================================

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { LoadingContext, MButton, MInputTextField } from "src/M_Components";
import { Paginator } from "primereact/paginator";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import MUploadFiles from "src/M_Components/M_UploadFiles/M_UploadFiles";
import TableHeader from "../../components/tableHelpers/TableHeader";
import ColumnMenu from "../../components/tableHelpers/ColumnMenu";
import ViewsMenu from "../../components/tableHelpers/ViewMenu";
import { AdvanceFilter } from "src/M_Components/M_Table/advanceFilter/AdvanceFilter";
import {
  headerCellStyle,
  subHeaderCellStyle,
  cellStyle,
} from "../../components/tableHelpers/TableStyleUtils";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import { useTableFilters } from "../../components/tableHelpers/useTableFilters";

import "./InviteTableSection.css";

//region Constants

const ALL_COLUMNS = [
  "Origin",
  "Destination",
  "BidValue",
  "Loads",
  "EstimatedTransitDays",
];

const SINGLE_COLUMNS = [
  "Origin",
  "Destination",
  "BidValue",
  "Loads",
  "EstimatedTransitDays",
];

const COLUMN_GROUPS = [];

//endregion

//region Input normalization helpers

/**
 * Normalizes a bid input into a decimal-compatible string.
 *
 * Purpose in this file:
 * - allows digits and one decimal separator;
 * - converts comma decimal separators to dots;
 * - removes currency symbols and other non-numeric characters from user input.
 *
 * @param {any} raw - Raw input value.
 * @returns {string} Normalized decimal string.
 */
const normalizeBidInput = (raw) => {
  if (raw === "" || raw == null) return "";

  let value = String(raw).replace(",", ".");
  value = value.replace(/[^0-9.]/g, "");

  const parts = value.split(".");

  if (parts.length > 2) {
    value = `${parts[0]}.${parts.slice(1).join("")}`;
  }

  return value;
};

/**
 * Normalizes a loads input into an integer-compatible string.
 *
 * Purpose in this file:
 * - keeps Loads as a whole-number field;
 * - removes decimal separators, signs and non-numeric characters.
 *
 * @param {any} raw - Raw input value.
 * @returns {string} Normalized integer string.
 */
const normalizeLoadsInput = (raw) => {
  if (raw === "" || raw == null) return "";

  return String(raw).replace(/[^\d]/g, "");
};

/**
 * Normalizes an Estimated Transit Days input into an integer-compatible string.
 *
 * Purpose in this file:
 * - keeps ETD as a whole-number field;
 * - removes decimal separators, signs and non-numeric characters.
 *
 * @param {any} raw - Raw input value.
 * @returns {string} Normalized integer string.
 */
const normalizeEtdInput = (raw) => {
  if (raw === "" || raw == null) return "";

  return String(raw).replace(/[^\d]/g, "");
};

/**
 * Blocks scientific notation and sign characters in decimal fields.
 *
 * Purpose in this file:
 * - prevents browser number inputs from accepting values such as "1e5";
 * - keeps Bid Value input compatible with the backend decimal expectations.
 *
 * @param {KeyboardEvent} e - Keyboard event.
 * @returns {void}
 */
const blockExpSigns = (e) => {
  if (["e", "E", "+", "-"].includes(e.key)) {
    e.preventDefault();
  }
};

/**
 * Blocks decimal and sign characters in integer-only fields.
 *
 * Purpose in this file:
 * - keeps Loads and ETD inputs as positive integer fields;
 * - prevents decimal, scientific notation and signed values.
 *
 * @param {KeyboardEvent} e - Keyboard event.
 * @returns {void}
 */
const blockNonIntegerKeys = (e) => {
  if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
    e.preventDefault();
  }
};

//endregion

//region Excel helpers

/**
 * Automatically sizes worksheet columns based on their longest rendered value.
 *
 * Purpose in this file:
 * - improves readability of exported quote templates;
 * - avoids clipped Origin, Destination and numeric columns.
 *
 * @param {object} worksheet - ExcelJS worksheet instance.
 * @returns {void}
 */
const autoFitColumns = (worksheet) => {
  (worksheet.columns || []).forEach((column) => {
    let max = 10;

    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const value = cell.value == null ? "" : String(cell.value);
      max = Math.max(max, value.length + 2);
    });

    column.width = max;
  });
};

/**
 * Converts a base64 upload payload into an ArrayBuffer.
 *
 * Purpose in this file:
 * - allows ExcelJS to read files returned by MUploadFiles;
 * - supports both raw base64 strings and data URL formatted strings.
 *
 * @param {string} dataUrlOrBase64 - Raw base64 or data URL value.
 * @returns {ArrayBuffer} Decoded binary file content.
 */
const base64ToArrayBuffer = (dataUrlOrBase64) => {
  const base64 = (dataUrlOrBase64 || "").includes(",")
    ? dataUrlOrBase64.split(",")[1]
    : dataUrlOrBase64;

  const binary = atob(base64);
  const len = binary.length;
  const buffer = new ArrayBuffer(len);
  const view = new Uint8Array(buffer);

  for (let i = 0; i < len; i += 1) {
    view[i] = binary.charCodeAt(i);
  }

  return buffer;
};

//endregion

//region Formatting helpers

/**
 * Normalizes a currency symbol for visual rendering.
 *
 * Purpose in this file:
 * - prevents null/undefined currency values from being rendered;
 * - keeps Bid Value suffix rendering stable.
 *
 * @param {any} value - Raw currency symbol/code.
 * @returns {string} Printable currency label.
 */
const normalizeCurrencySymbol = (value) => String(value ?? "").trim();

/**
 * Returns a stable display value for read-only cells.
 *
 * Purpose in this file:
 * - keeps read-only numeric cells aligned with editable inputs;
 * - supports optional suffix rendering for currency values.
 *
 * @param {any} value - Raw cell value.
 * @param {string} suffix - Optional suffix to append after the value.
 * @returns {string} Display text.
 */
const formatReadonlyValue = (value, suffix = "") => {
  if (value === "" || value == null) return "-";

  const normalizedSuffix = normalizeCurrencySymbol(suffix);

  return normalizedSuffix ? `${value} ${normalizedSuffix}` : String(value);
};

//endregion

//region Component

/**
 * Renders the carrier invite bid table section.
 *
 * Purpose in this file:
 * - manages the local bid draft per lane;
 * - validates create/edit submissions;
 * - delegates persistence to services injected by CarrierSubmitPage;
 * - provides table utilities such as refresh, Excel import/export, filters,
 *   saved views, column visibility and pagination.
 *
 * @param {object} props - Component props.
 * @param {string|null} props.token - Optional invite token for public invite flows.
 * @param {"spot"|"phase"} props.mode - Quote target mode.
 * @param {Array} props.lanes - Lane collection from the API.
 * @param {"create"|"edit"|"view"} props.crudMode - Current grid behavior mode.
 * @param {boolean} props.readOnly - Whether the table should be non-editable.
 * @param {Array} props.prefillQuotes - Existing quotes used in edit/view modes.
 * @param {object} props.submitServices - Batch submit services.
 * @param {object} props.editServices - Single create/update services.
 * @param {string|number|null} props.tenderId - Tender id.
 * @param {string|number|null} props.tenderPhaseId - TenderPhase id.
 * @param {string|number|null} props.carrierId - Carrier id.
 * @param {string|number} props.tenderLoads - Tender target loads.
 * @param {object} props.acceptedLoadsByLane - Accepted loads indexed by lane id.
 * @param {string} props.currencySymbol - Optional Bid Value currency suffix.
 * @param {Function} props.onRefresh - Refresh callback.
 * @param {Function} props.onBatchSubmitSuccess - Callback after successful batch submit.
 * @returns {JSX.Element} Carrier invite table section.
 */
export default function InviteTableSection({
  token,
  mode,
  lanes,
  crudMode,
  readOnly = false,
  prefillQuotes = [],
  submitServices,
  editServices,
  tenderId,
  tenderPhaseId,
  carrierId,
  tenderLoads = 0,
  acceptedLoadsByLane = {},
  currencySymbol = "",
  onRefresh,
  onBatchSubmitSuccess,
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);

  const isCreateMode = crudMode === "create";
  const isEditMode = crudMode === "edit";
  const isViewMode = crudMode === "view";

  //endregion

  //region Local state

  const [rows, setRows] = useState([]);
  const [baseline, setBaseline] = useState({});
  const [quoteIdByLane, setQuoteIdByLane] = useState({});
  const [draft, setDraft] = useState({});
  const [status, setStatus] = useState({});
  const [activeLaneId, setActiveLaneId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState(ALL_COLUMNS);

  const [first, setFirst] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [anchorEls, setAnchorEls] = useState(null);
  const [anchorAF, setAnchorAF] = useState(null);
  const [anchorV, setAnchorV] = useState(null);

  const [showImporter, setShowImporter] = useState(false);

  const currencyLabel = useMemo(
    () => normalizeCurrencySymbol(currencySymbol),
    [currencySymbol]
  );

  //endregion

  //region Filters and saved views

  const {
    metaPair,
    views,
    addMetaPair,
    deleteFilter,
    deleteAllFilters,
    submitFilters,
    resetToDefault,
    applyView,
    saveView,
  } = useTableFilters({
    /**
     * Resets pagination after filter submission.
     *
     * Purpose in this file:
     * - prevents the paginator from staying on an empty page after filters reduce
     *   the visible result set.
     *
     * @returns {void}
     */
    onSubmit: () => setFirst(0),
  });

  const metaType = useMemo(
    () => [
      {
        metaName: "origin",
        metaDisplay: "Origin",
        metaType: "string",
        metaIsList: false,
      },
      {
        metaName: "destination",
        metaDisplay: "Destination",
        metaType: "string",
        metaIsList: false,
      },
    ],
    []
  );

  //endregion

  //region Capacity calculations

  const maxLoadsByLane = useMemo(() => {
    const cap = Number(tenderLoads) || 0;
    const map = {};

    for (const row of rows || []) {
      const laneId = row?.laneId;

      if (!laneId) continue;

      const accepted = Number(acceptedLoadsByLane?.[laneId] ?? 0) || 0;
      const remaining = Math.max(0, cap - accepted);

      map[laneId] = remaining;
    }

    return map;
  }, [rows, tenderLoads, acceptedLoadsByLane]);

  const loadsExceedByLane = useMemo(() => {
    const out = {};

    Object.entries(draft || {}).forEach(([key, value]) => {
      const laneId = Number(key);

      if (!laneId) return;

      const max = Number(maxLoadsByLane?.[laneId] ?? 0);
      const rawLoads = value?.loads;
      const loads = Number(String(rawLoads ?? ""));

      if (!Number.isFinite(loads) || rawLoads === "" || rawLoads == null) {
        return;
      }

      if (max > 0 && loads > max) {
        out[laneId] = true;
      }

      if (max === 0 && loads > 0) {
        out[laneId] = true;
      }
    });

    return out;
  }, [draft, maxLoadsByLane]);

  //endregion

  //region Lane normalization

  useEffect(() => {
    if (!lanes || lanes.length === 0) {
      setRows([]);
      setBaseline({});
      setDraft({});
      setQuoteIdByLane({});
      setStatus({});
      setActiveLaneId(null);
      setFirst(0);
      return;
    }

    const normalized = (lanes || []).map((lane) => {
      const laneId =
        lane?.laneId ??
        lane?.LaneId ??
        lane?.laneID ??
        lane?.LaneID ??
        lane?.Id ??
        lane?.id ??
        null;

      const origin =
        lane?.sourceLocation ??
        lane?.SourceLocation ??
        lane?.Origin ??
        lane?.origin ??
        lane?.Source ??
        lane?.source ??
        lane?.LaneSource ??
        lane?.laneSource ??
        "";

      const destination =
        lane?.destinationLocation ??
        lane?.DestinationLocation ??
        lane?.Destination ??
        lane?.destination ??
        lane?.DestinationName ??
        lane?.destinationName ??
        lane?.LaneDestination ??
        lane?.laneDestination ??
        "";

      return {
        laneId: laneId != null ? Number(laneId) : null,
        origin: String(origin || "").trim(),
        destination: String(destination || "").trim(),
      };
    });

    const filtered = normalized.filter(
      (row) => row.laneId && row.origin && row.destination
    );

    filtered.sort((a, b) => {
      const destinationComparison = a.destination.localeCompare(b.destination);

      return destinationComparison !== 0
        ? destinationComparison
        : a.origin.localeCompare(b.origin);
    });

    setRows(filtered);
    setFirst(0);
  }, [lanes]);

  //endregion

  //region Quote prefill

  useEffect(() => {
    if (!prefillQuotes || prefillQuotes.length === 0) {
      if (isEditMode || isViewMode) {
        setBaseline({});
        setDraft({});
        setQuoteIdByLane({});
        setActiveLaneId(null);
        setStatus({});
      }

      return;
    }

    const nextBaseline = {};
    const nextIds = {};

    for (const quote of prefillQuotes) {
      const laneId = Number(quote?.LaneId ?? quote?.laneId ?? null);

      if (!laneId) continue;

      const bidRaw = quote?.BidValue ?? quote?.bidValue;
      const loadsRaw = quote?.Loads ?? quote?.loads;

      const etdRaw =
        quote?.EstimatedTransitDays ??
        quote?.estimatedTransitDays ??
        quote?.etd;

      nextBaseline[laneId] = {
        bidValue: bidRaw != null ? String(bidRaw) : "",
        loads: loadsRaw != null ? String(loadsRaw) : "",
        estimatedTransitDays: etdRaw != null ? String(etdRaw) : "",
      };

      const quoteId = quote?.QuoteId ?? quote?.Id ?? quote?.id;

      if (quoteId != null) {
        nextIds[laneId] = Number(quoteId);
      }
    }

    setBaseline(nextBaseline);
    setDraft(nextBaseline);
    setQuoteIdByLane(nextIds);
    setActiveLaneId(null);
    setStatus({});
  }, [prefillQuotes, isEditMode, isViewMode]);

  //endregion

  //region Filtering and pagination

  const filteredRows = useMemo(() => {
    let data = [...rows];

    metaPair.forEach((filter) => {
      const value = String(filter.value || "").toLowerCase();

      if (!value) return;

      if (filter.name === "origin") {
        if (filter.operation === "Contains") {
          data = data.filter((row) => row.origin.toLowerCase().includes(value));
        }

        if (filter.operation === "Equals") {
          data = data.filter((row) => row.origin.toLowerCase() === value);
        }
      }

      if (filter.name === "destination") {
        if (filter.operation === "Contains") {
          data = data.filter((row) =>
            row.destination.toLowerCase().includes(value)
          );
        }

        if (filter.operation === "Equals") {
          data = data.filter((row) => row.destination.toLowerCase() === value);
        }
      }
    });

    return data;
  }, [rows, metaPair]);

  const pagedRows = useMemo(
    () => filteredRows.slice(first, first + rowsPerPage),
    [filteredRows, first, rowsPerPage]
  );

  //endregion

  //region Editability helpers

  /**
   * Determines whether the editable cells for a lane should be disabled.
   *
   * Purpose in this file:
   * - blocks editing while submit is running;
   * - blocks editing in read-only mode;
   * - keeps successfully submitted create rows locked;
   * - restricts edit mode to one active lane at a time.
   *
   * @param {number|null} laneId - Lane identifier.
   * @returns {boolean} True when the lane cells should be disabled.
   */
  const isDisabledCell = useCallback(
    (laneId) => {
      if (!laneId || submitting || readOnly) return true;
      if (isCreateMode && status[laneId]?.ok) return true;
      if (isEditMode) return laneId !== activeLaneId;

      return false;
    },
    [submitting, readOnly, isCreateMode, isEditMode, status, activeLaneId]
  );

  /**
   * Activates one lane for editing.
   *
   * Purpose in this file:
   * - keeps edit mode controlled and predictable;
   * - restores the baseline when switching from one lane to another.
   *
   * @param {number|null} laneId - Lane identifier.
   * @returns {void}
   */
  const activateLane = (laneId) => {
    if (!isEditMode || readOnly) return;
    if (!laneId) return;
    if (laneId === activeLaneId) return;

    setDraft({ ...baseline });
    setStatus({});
    setActiveLaneId(laneId);
  };

  //endregion

  //region Draft mutation handlers

  /**
   * Updates the Bid Value draft for one lane.
   *
   * @param {number} laneId - Lane identifier.
   * @param {any} value - Raw input value.
   * @returns {void}
   */
  const onChangeBid = (laneId, value) =>
    setDraft((prev) => ({
      ...prev,
      [laneId]: {
        ...(prev[laneId] || {}),
        bidValue: normalizeBidInput(value),
      },
    }));

  /**
   * Updates the Loads draft for one lane.
   *
   * @param {number} laneId - Lane identifier.
   * @param {any} value - Raw input value.
   * @returns {void}
   */
  const onChangeLoads = (laneId, value) =>
    setDraft((prev) => ({
      ...prev,
      [laneId]: {
        ...(prev[laneId] || {}),
        loads: normalizeLoadsInput(value),
      },
    }));

  /**
   * Updates the Estimated Transit Days draft for one lane.
   *
   * @param {number} laneId - Lane identifier.
   * @param {any} value - Raw input value.
   * @returns {void}
   */
  const onChangeEstimatedTransitDays = (laneId, value) =>
    setDraft((prev) => ({
      ...prev,
      [laneId]: {
        ...(prev[laneId] || {}),
        estimatedTransitDays: normalizeEtdInput(value),
      },
    }));

  //endregion

  //region Change detection

  const hasAnyDraftValues = useMemo(
    () =>
      Object.values(draft).some(
        (value) =>
          (value?.bidValue != null && value.bidValue !== "") ||
          (value?.loads != null && value.loads !== "") ||
          (value?.estimatedTransitDays != null &&
            value.estimatedTransitDays !== "")
      ),
    [draft]
  );

  const hasActiveChanges = useMemo(() => {
    if (!isEditMode || !activeLaneId) return false;

    const current = draft[activeLaneId] || {
      bidValue: "",
      loads: "",
      estimatedTransitDays: "",
    };

    const original = baseline[activeLaneId] || {
      bidValue: "",
      loads: "",
      estimatedTransitDays: "",
    };

    return (
      (current.bidValue ?? "") !== (original.bidValue ?? "") ||
      (current.loads ?? "") !== (original.loads ?? "") ||
      (current.estimatedTransitDays ?? "") !==
      (original.estimatedTransitDays ?? "")
    );
  }, [isEditMode, activeLaneId, draft, baseline]);

  //endregion

  //region Validation

  /**
   * Validates create-mode draft values and builds a batch payload.
   *
   * Purpose in this file:
   * - ignores completely empty rows;
   * - rejects partially filled rows;
   * - validates numeric values;
   * - enforces remaining lane capacity.
   *
   * @returns {{errors:Array<string>, payload:Array<object>}} Validation result.
   */
  const validateBatch = () => {
    const errors = [];
    const payload = [];

    for (const [key, value] of Object.entries(draft)) {
      const laneId = Number(key);

      if (!laneId) continue;

      const bidStr = value?.bidValue;
      const loadsStr = value?.loads;
      const etdStr = value?.estimatedTransitDays;

      const hasBid = bidStr !== "" && bidStr != null;
      const hasLoads = loadsStr !== "" && loadsStr != null;
      const hasEtd = etdStr !== "" && etdStr != null;

      if (!hasBid && !hasLoads && !hasEtd) continue;

      if (!hasBid || !hasLoads || !hasEtd) {
        errors.push(t("AllQuoteFieldsRequired"));
        continue;
      }

      const bidNum = Number(String(bidStr).replace(",", "."));
      const loadsNum = Number(String(loadsStr));
      const etdNum = Number(String(etdStr));

      if (!Number.isFinite(bidNum) || bidNum <= 0) {
        errors.push(t("InvalidBidValue"));
        continue;
      }

      if (
        !Number.isFinite(loadsNum) ||
        !Number.isInteger(loadsNum) ||
        loadsNum <= 0
      ) {
        errors.push(t("InvalidLoadsValue"));
        continue;
      }

      if (
        !Number.isFinite(etdNum) ||
        !Number.isInteger(etdNum) ||
        etdNum <= 0
      ) {
        errors.push(t("InvalidEstimatedTransitDays"));
        continue;
      }

      const maxForLane = Number(maxLoadsByLane?.[laneId] ?? 0);

      if (loadsNum > maxForLane) {
        errors.push(t("LoadsExceedRemainingCapacity"));
        continue;
      }

      payload.push({
        laneId,
        bidValue: bidNum,
        loads: loadsNum,
        estimatedTransitDays: etdNum,
      });
    }

    return { errors, payload };
  };

  /**
   * Validates one edit-mode lane and builds a single-quote payload.
   *
   * Purpose in this file:
   * - validates only the active lane;
   * - rejects incomplete or invalid numeric values;
   * - enforces remaining lane capacity before create/update.
   *
   * @param {number} laneId - Active lane identifier.
   * @returns {{errors:Array<string>, payload:object|null}} Validation result.
   */
  const validateSingleLane = (laneId) => {
    const errors = [];
    const value = draft[laneId] || {};

    const bidStr = value?.bidValue;
    const loadsStr = value?.loads;
    const etdStr = value?.estimatedTransitDays;

    const hasBid = bidStr !== "" && bidStr != null;
    const hasLoads = loadsStr !== "" && loadsStr != null;
    const hasEtd = etdStr !== "" && etdStr != null;

    if (!hasBid || !hasLoads || !hasEtd) {
      errors.push(t("AllQuoteFieldsRequired"));
      return { errors, payload: null };
    }

    const bidNum = Number(String(bidStr).replace(",", "."));
    const loadsNum = Number(String(loadsStr));
    const etdNum = Number(String(etdStr));

    if (!Number.isFinite(bidNum) || bidNum <= 0) {
      errors.push(t("InvalidBidValue"));
    }

    if (
      !Number.isFinite(loadsNum) ||
      !Number.isInteger(loadsNum) ||
      loadsNum <= 0
    ) {
      errors.push(t("InvalidLoadsValue"));
    }

    if (!Number.isFinite(etdNum) || !Number.isInteger(etdNum) || etdNum <= 0) {
      errors.push(t("InvalidEstimatedTransitDays"));
    }

    const maxForLane = Number(maxLoadsByLane?.[laneId] ?? 0);

    if (Number.isFinite(loadsNum) && loadsNum > 0 && loadsNum > maxForLane) {
      errors.push(t("LoadsExceedRemainingCapacity"));
    }

    if (errors.length) {
      return { errors, payload: null };
    }

    return {
      errors,
      payload: {
        laneId,
        bidValue: bidNum,
        loads: loadsNum,
        estimatedTransitDays: etdNum,
      },
    };
  };

  //endregion

  //region Submission

  /**
   * Submits the current grid draft.
   *
   * Purpose in this file:
   * - sends a batch request in create mode;
   * - creates or updates one quote in edit mode;
   * - refreshes data after successful persistence;
   * - keeps read-only, submitting and invalid modes inert.
   *
   * @returns {Promise<void>}
   */
  const onSubmitAll = async () => {
    if (submitting || readOnly || !mode) return;

    if (isCreateMode) {
      const { errors, payload } = validateBatch();

      if (errors.length) {
        ctx.toastMessages("error", "", errors.join(" - "));
        return;
      }

      if (payload.length === 0) {
        ctx.toastMessages("warn", "", t("NothingToSubmit"));
        return;
      }

      ctx.setIsLoad(true);
      setSubmitting(true);

      try {
        const service =
          mode === "spot"
            ? submitServices?.spotBatch
            : submitServices?.phaseBatch;

        if (!service || typeof service.post !== "function") {
          ctx.toastMessages("error", "", t("SubmitServiceNotConfigured"));
          setSubmitting(false);
          return;
        }

        const request = token ? { token, items: payload } : { items: payload };
        const res = await service.post(request);

        if (isFailurePayload(res) || res?.IsFailure) {
          ctx.toastMessages(
            "error",
            "",
            apiMessage(res, t("ErrorSaving") || "ErrorSaving")
          );
          return;
        }

        const nextStatus = { ...status };

        for (const item of payload) {
          nextStatus[item.laneId] = { ok: true };
        }

        setStatus(nextStatus);
        ctx.toastMessages("success", "", t("SubmissionCompleted"));

        await onRefresh?.();

        if (typeof onBatchSubmitSuccess === "function") {
          onBatchSubmitSuccess();
        }
      } catch (err) {
        ctx.toastMessages(
          "error",
          "",
          apiMessage(err, t("ErrorSaving") || "ErrorSaving")
        );
      } finally {
        setSubmitting(false);
        ctx.setIsLoad(false);
      }

      return;
    }

    if (isEditMode) {
      if (!activeLaneId) {
        ctx.toastMessages("warn", "", t("SelectRowToEdit"));
        return;
      }

      const { errors, payload } = validateSingleLane(activeLaneId);

      if (errors.length || !payload) {
        ctx.toastMessages("error", "", errors.join(" - "));
        return;
      }

      if (!carrierId) {
        ctx.toastMessages("error", "", t("MissingCarrierId"));
        return;
      }

      ctx.setIsLoad(true);
      setSubmitting(true);

      try {
        const serviceGroup =
          mode === "spot" ? editServices?.spot : editServices?.phase;

        if (!serviceGroup) {
          ctx.toastMessages("error", "", t("EditServiceNotConfigured"));
          setSubmitting(false);
          return;
        }

        const existingQuoteId = quoteIdByLane[activeLaneId];
        let res;

        if (existingQuoteId) {
          const body = {
            bidValue: payload.bidValue,
            loads: payload.loads,
            estimatedTransitDays: payload.estimatedTransitDays,
          };

          res = await serviceGroup.update(existingQuoteId, body);
        } else {
          if (mode === "spot" && !tenderId) {
            ctx.toastMessages("error", "", t("MissingTenderId"));
            setSubmitting(false);
            return;
          }

          if (mode === "phase" && !tenderPhaseId) {
            ctx.toastMessages("error", "", t("MissingTenderPhaseId"));
            setSubmitting(false);
            return;
          }

          const baseBody = {
            laneId: payload.laneId,
            bidValue: payload.bidValue,
            loads: payload.loads,
            estimatedTransitDays: payload.estimatedTransitDays,
            carrierId: Number(carrierId),
          };

          const body =
            mode === "spot"
              ? {
                ...baseBody,
                tenderId: Number(tenderId),
              }
              : {
                ...baseBody,
                ...(tenderId != null ? { tenderId: Number(tenderId) } : {}),
                tenderPhaseId: Number(tenderPhaseId),
              };

          res = await serviceGroup.create(body);
        }

        if (isFailurePayload(res) || res?.IsFailure) {
          ctx.toastMessages(
            "error",
            "",
            apiMessage(res, t("ErrorSaving") || "ErrorSaving")
          );
          return;
        }

        ctx.toastMessages("success", "", t("SubmissionCompleted"));

        await onRefresh?.();

        setActiveLaneId(null);
        setStatus({});
      } catch (err) {
        ctx.toastMessages(
          "error",
          "",
          apiMessage(err, t("ErrorSaving") || "ErrorSaving")
        );
      } finally {
        setSubmitting(false);
        ctx.setIsLoad(false);
      }
    }
  };

  //endregion

  //region UI flags and actions

  const canSubmitCreate =
    isCreateMode &&
    !submitting &&
    !readOnly &&
    !!lanes?.length &&
    hasAnyDraftValues;

  const canSubmitEdit =
    isEditMode &&
    !submitting &&
    !readOnly &&
    !!activeLaneId &&
    hasActiveChanges;

  const canClearCreate =
    isCreateMode && !submitting && hasAnyDraftValues && !readOnly;

  const canClearEdit =
    isEditMode &&
    !submitting &&
    !readOnly &&
    !!activeLaneId &&
    hasActiveChanges;

  const isSubmitDisabled =
    submitting || readOnly || (!canSubmitCreate && !canSubmitEdit);

  /**
   * Submits the current table when Enter is pressed inside an editable input.
   *
   * Purpose in this file:
   * - keeps keyboard submission aligned with the Submit button behavior;
   * - prevents Enter from bubbling into external key handlers;
   * - keeps disabled submit states inert.
   *
   * @param {KeyboardEvent} event - Keyboard event emitted from an input wrapper.
   * @returns {void}
   */
  const handleSubmitShortcut = (event) => {
    if (event.key !== "Enter") return;

    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent?.stopImmediatePropagation?.();

    if (isSubmitDisabled) return;

    onSubmitAll();
  };

  /**
   * Clears the editable draft state.
   *
   * Purpose in this file:
   * - restores server baseline in edit mode;
   * - clears every draft value in create mode.
   *
   * @returns {void}
   */
  const handleClear = () => {
    if (isEditMode) {
      setDraft({ ...baseline });
      setStatus({});
      setActiveLaneId(null);
      return;
    }

    setDraft({});
    setStatus({});
  };

  /**
   * Refreshes external data and resets local transient table state.
   *
   * Purpose in this file:
   * - delegates the real data reload to CarrierSubmitPage;
   * - resets pagination and edit state after refresh;
   * - supports create mode because the parent refreshes lanes and capacity too.
   *
   * @returns {Promise<void>}
   */
  const handleRefresh = async () => {
    ctx.setIsLoad(true);

    try {
      if (typeof onRefresh === "function") {
        await onRefresh();
      }

      setFirst(0);
      setStatus({});
      setActiveLaneId(null);
    } finally {
      ctx.setIsLoad(false);
    }
  };

  //endregion

  //region Excel export

  /**
   * Exports the visible filtered rows to an Excel workbook.
   *
   * Purpose in this file:
   * - produces a carrier quote template in create mode;
   * - produces a quote snapshot in edit/view modes;
   * - protects non-editable cells and validates numeric editable cells.
   *
   * @returns {Promise<void>}
   */
  const handleExport = async () => {
    ctx.setIsLoad(true);

    try {
      if (!filteredRows.length) {
        ctx.toastMessages("warn", "", t("NothingToExport"));
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("CarrierInvite");

      worksheet.addRow([
        t("Origin"),
        t("Destination"),
        t("BidValue"),
        t("Loads"),
        t("EstimatedTransitDays"),
      ]);

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.views = [{ state: "frozen", ySplit: 1, xSplit: 0 }];

      filteredRows.forEach((lane) => {
        const laneDraft = draft[lane.laneId] || {};
        const bid = laneDraft.bidValue ?? "";
        const loads = laneDraft.loads ?? "";
        const etd = laneDraft.estimatedTransitDays ?? "";

        worksheet.addRow([
          lane.origin,
          lane.destination,
          bid === "" ? null : Number(bid),
          loads === "" ? null : Number(loads),
          etd === "" ? null : Number(etd),
        ]);
      });

      worksheet.eachRow((row) =>
        row.eachCell((cell) => {
          cell.protection = { locked: true };
        })
      );

      if (!readOnly && isCreateMode) {
        for (let rowIndex = 2; rowIndex <= worksheet.rowCount; rowIndex += 1) {
          const bidCell = worksheet.getCell(rowIndex, 3);
          const loadsCell = worksheet.getCell(rowIndex, 4);
          const etdCell = worksheet.getCell(rowIndex, 5);

          bidCell.protection = { locked: false };
          loadsCell.protection = { locked: false };
          etdCell.protection = { locked: false };

          bidCell.dataValidation = {
            type: "decimal",
            operator: "greaterThan",
            formula1: 0,
            allowBlank: true,
            showInputMessage: true,
            promptTitle: t("BidValue"),
            prompt: t("EnterPositiveDecimal"),
          };

          const rowLaneId = filteredRows?.[rowIndex - 2]?.laneId;
          const maxForLane = Number(maxLoadsByLane?.[rowLaneId] ?? 0);

          loadsCell.dataValidation = {
            type: "whole",
            operator: "between",
            formula1: 1,
            formula2: Math.max(1, maxForLane),
            allowBlank: true,
            showInputMessage: true,
            promptTitle: t("Loads"),
            prompt: t("EnterPositiveInteger"),
          };

          etdCell.dataValidation = {
            type: "whole",
            operator: "greaterThan",
            formula1: 0,
            allowBlank: true,
            showInputMessage: true,
            promptTitle: t("EstimatedTransitDays"),
            prompt: t("EnterPositiveInteger"),
          };
        }
      }

      autoFitColumns(worksheet);

      await worksheet.protect("carrier_invite", {
        selectLockedCells: true,
        selectUnlockedCells: !readOnly && isCreateMode,
      });

      const buffer = await workbook.xlsx.writeBuffer();

      const fileName =
        mode === "spot"
          ? readOnly || isEditMode
            ? "SpotQuotes.xlsx"
            : "CarrierInviteTemplate.xlsx"
          : readOnly || isEditMode
            ? "PhaseQuotes.xlsx"
            : "CarrierInviteTemplate.xlsx";

      saveAs(new Blob([buffer]), fileName);
      ctx.toastMessages("success", "", t("Excel exported"));
    } catch {
      ctx.toastMessages("error", "", t("Error exporting Excel"));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  //endregion

  //region Excel import

  /**
   * Imports quote draft values from an Excel workbook.
   *
   * Purpose in this file:
   * - supports create-mode batch input;
   * - maps workbook rows to lanes by Origin and Destination;
   * - validates imported Bid Value, Loads and ETD before updating the draft.
   *
   * @param {Array} files - Files emitted by MUploadFiles.
   * @returns {Promise<void>}
   */
  const handleFilesChange = async (files) => {
    ctx.setIsLoad(true);

    try {
      if (!files || !files.length) return;

      const file = files[0];
      const name = (file?.name || "").toLowerCase();

      if (!name.endsWith(".xlsx")) {
        ctx.toastMessages("error", "", t("PleaseUploadXlsx"));
        return;
      }

      const arrayBuffer = base64ToArrayBuffer(file.base64);
      const workbook = new ExcelJS.Workbook();

      await workbook.xlsx.load(arrayBuffer);

      const worksheet =
        workbook.getWorksheet("CarrierInvite") || workbook.worksheets[0];

      if (!worksheet) {
        ctx.toastMessages("error", "", t("InvalidExcelNoWorksheet"));
        return;
      }

      const headerRow = worksheet.getRow(1);
      const headerMap = {};

      headerRow.eachCell((cell, columnIndex) => {
        const key = String(cell.value || "").trim().toLowerCase();

        if (key) {
          headerMap[key] = columnIndex;
        }
      });

      const originKey = (t("Origin") || "Origin").trim().toLowerCase();
      const destinationKey = (t("Destination") || "Destination")
        .trim()
        .toLowerCase();
      const bidValueKey = (t("BidValue") || "BidValue").trim().toLowerCase();
      const bidKey = (t("Bid") || "Bid").trim().toLowerCase();
      const loadsKey = (t("Loads") || "Loads").trim().toLowerCase();
      const etdKey = (t("EstimatedTransitDays") || "EstimatedTransitDays")
        .trim()
        .toLowerCase();

      const originCol = headerMap["origin"] ?? headerMap[originKey];

      const destinationCol =
        headerMap["destination"] ?? headerMap[destinationKey];

      const bidCol =
        headerMap["bidvalue"] ??
        headerMap["bid"] ??
        headerMap[bidValueKey] ??
        headerMap[bidKey];

      const loadsCol = headerMap["loads"] ?? headerMap[loadsKey];

      const etdCol =
        headerMap["estimatedtransitdays"] ??
        headerMap["etd"] ??
        headerMap[etdKey];

      if (!originCol || !destinationCol || !bidCol || !loadsCol || !etdCol) {
        ctx.toastMessages("error", "", t("InvalidTemplateHeader"));
        return;
      }

      /**
       * Converts an Excel cell value into a number when possible.
       *
       * @param {any} value - Raw Excel cell value.
       * @returns {number|null} Numeric value or null.
       */
      const toNum = (value) => {
        if (value == null || value === "") return null;
        if (typeof value === "number") return value;

        const numericValue = Number(String(value).replace(",", "."));

        return Number.isFinite(numericValue) ? numericValue : null;
      };

      const laneIndex = new Map();

      rows.forEach((lane) => {
        const key = `${lane.origin}|||${lane.destination}`;

        if (!laneIndex.has(key)) {
          laneIndex.set(key, lane.laneId);
        }
      });

      const updates = {};

      for (let rowIndex = 2; rowIndex <= worksheet.rowCount; rowIndex += 1) {
        const originRaw = worksheet.getCell(rowIndex, originCol).value;
        const destinationRaw = worksheet.getCell(rowIndex, destinationCol).value;

        if (!originRaw && !destinationRaw) continue;

        const origin = String(originRaw || "").trim();
        const destination = String(destinationRaw || "").trim();

        if (!origin || !destination) continue;

        const key = `${origin}|||${destination}`;
        const laneId = laneIndex.get(key);

        if (!laneId) continue;

        const bidValue = toNum(worksheet.getCell(rowIndex, bidCol).value);
        const loadsValue = toNum(worksheet.getCell(rowIndex, loadsCol).value);
        const etdValue = toNum(worksheet.getCell(rowIndex, etdCol).value);

        if (bidValue == null && loadsValue == null && etdValue == null) {
          continue;
        }

        if (
          bidValue == null ||
          loadsValue == null ||
          etdValue == null ||
          !Number.isFinite(bidValue) ||
          !Number.isFinite(loadsValue) ||
          !Number.isFinite(etdValue) ||
          !Number.isInteger(loadsValue) ||
          !Number.isInteger(etdValue) ||
          bidValue <= 0 ||
          loadsValue <= 0 ||
          etdValue <= 0
        ) {
          ctx.toastMessages(
            "warning",
            "",
            `${t("InvalidValuesAt")} ${origin} - ${destination}`
          );
          continue;
        }

        const maxForLane = Number(maxLoadsByLane?.[laneId] ?? 0);

        if (loadsValue > maxForLane) {
          ctx.toastMessages(
            "warning",
            "",
            `${t("LoadsExceedRemainingCapacity")} ${origin} - ${destination}`
          );
          continue;
        }

        updates[laneId] = {
          bidValue: String(bidValue),
          loads: String(loadsValue),
          estimatedTransitDays: String(etdValue),
        };
      }

      if (Object.keys(updates).length === 0) {
        ctx.toastMessages("warn", "", t("NoValidDataInExcel"));
        return;
      }

      setDraft((prev) => {
        const next = { ...prev };

        Object.entries(updates).forEach(([laneId, value]) => {
          next[laneId] = {
            ...(next[laneId] || {}),
            bidValue: normalizeBidInput(value.bidValue),
            loads: normalizeLoadsInput(value.loads),
            estimatedTransitDays: normalizeEtdInput(value.estimatedTransitDays),
          };
        });

        return next;
      });

      ctx.toastMessages("success", "", t("ExcelImportedIntoGrid"));
      setShowImporter(false);
    } catch {
      ctx.toastMessages("error", "", t("ErrorReadingExcel"));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  //endregion

  //region Cell render helpers

  /**
   * Renders a read-only table cell with the same visual rhythm as inputs.
   *
   * Purpose in this file:
   * - keeps view mode aligned with editable mode;
   * - supports optional currency suffix display for Bid Value.
   *
   * @param {object} props - Component props.
   * @param {any} props.value - Cell value.
   * @param {"left"|"center"|"right"} props.align - Horizontal alignment.
   * @param {string} props.suffix - Optional suffix.
   * @returns {JSX.Element} Read-only cell.
   */
  const ViewCell = ({ value, align = "center", suffix = "" }) => (
    <div
      className="cip-viewcell"
      style={{
        minHeight: 36,
        display: "flex",
        alignItems: "center",
        justifyContent:
          align === "center"
            ? "center"
            : align === "right"
              ? "flex-end"
              : "flex-start",
        fontWeight: 500,
      }}
    >
      {formatReadonlyValue(value, suffix)}
    </div>
  );

  /**
   * Renders the editable Bid Value input with an optional currency suffix.
   *
   * Purpose in this file:
   * - keeps chart/table numeric behavior separated from currency rendering;
   * - displays values in the requested "input $" format;
   * - intercepts Enter before it reaches external key handlers.
   *
   * @param {object} args - Render arguments.
   * @param {number} args.laneId - Lane identifier.
   * @param {string} args.value - Current Bid Value draft.
   * @param {boolean} args.disabled - Disabled state.
   * @returns {JSX.Element} Bid input field.
   */
  const renderBidInput = ({ laneId, value, disabled }) => (
    <div className="cip-currency-input" onKeyDownCapture={handleSubmitShortcut}>
      <MInputTextField
        type="number"
        label=""
        value={value}
        onChange={(e) => onChangeBid(laneId, e.target.value)}
        onKeyDown={blockExpSigns}
        disabled={disabled}
      />

      {currencyLabel && (
        <span className="cip-currency-input-suffix">{currencyLabel}</span>
      )}
    </div>
  );

  ViewCell.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    align: PropTypes.oneOf(["left", "center", "right"]),
    suffix: PropTypes.string,
  };

  //endregion

  //region Render

  return (
    <div className="cip-main-table-div">
      <div className="cip-section">
        <TableHeader
          filterCount={metaPair.length}
          onRefresh={handleRefresh}
          onExport={handleExport}
          onImport={
            !readOnly && isCreateMode
              ? () => setShowImporter((value) => !value)
              : undefined
          }
          onOpenViews={(event) => setAnchorV(event.currentTarget)}
          onOpenFilters={(event) => setAnchorAF(event.currentTarget)}
          onOpenColumns={(event) => setAnchorEls(event.currentTarget)}
        />

        {!readOnly && isCreateMode && showImporter && (
          <div className="cip-import">
            <div className="cip-import-description">
              {t("UploadTheExcelTemplateFilledWithQuotesOnlyXlsx")}
            </div>

            <MUploadFiles
              multipleFiles={false}
              nameURL="carrier-invite-excel"
              onFilesChange={handleFilesChange}
            />
          </div>
        )}

        <ColumnMenu
          anchorEl={anchorEls}
          open={Boolean(anchorEls)}
          onClose={() => setAnchorEls(null)}
          singleColumns={SINGLE_COLUMNS}
          columnGroups={COLUMN_GROUPS}
          selectedColumns={selectedColumns}
          onChangeSelected={setSelectedColumns}
        />

        <ViewsMenu
          anchorEl={anchorV}
          open={Boolean(anchorV)}
          onClose={() => setAnchorV(null)}
          viewsMap={views}
          onSelectDefault={() => {
            resetToDefault();
            setAnchorV(null);
          }}
          onSelectView={(view) => {
            applyView(view);
            setAnchorV(null);
          }}
          onCreateNewViewClick={() => {
            setAnchorV(null);
            setAnchorAF(document.getElementById("advFilter"));
          }}
        />

        <AdvanceFilter
          metaPair={metaPair}
          metaType={metaType}
          onAddMetaPair={addMetaPair}
          onSubmitFilters={submitFilters}
          onDeleteFilter={deleteFilter}
          onDeleteAllFilters={deleteAllFilters}
          anchorAF={anchorAF}
          setAnchorAF={setAnchorAF}
          onSaveFilter={(viewName) => {
            saveView(viewName);
          }}
          views={views}
          onToasts={() => { }}
          color="var(--color-red)"
          removeFilters={["Add"]}
        />

        {!readOnly && (
          <div className="cip-actions-header table-header-root">
            <div className="table-header-left cip-actions-left">
              <MButton
                type="button"
                label={t("Submit")}
                icon={
                  <i
                    className="pi pi-send iconTable"
                    style={{ color: "var(--color-green-light)" }}
                  />
                }
                onClick={onSubmitAll}
                disabled={isSubmitDisabled}
              />

              <MButton
                type="button"
                label={t("Clear")}
                icon={
                  <i
                    className="pi pi-times iconTable"
                    style={{ color: "var(--color-red)" }}
                  />
                }
                onClick={handleClear}
                disabled={submitting || (!canClearCreate && !canClearEdit)}
              />
            </div>
          </div>
        )}

        <div className="cip-table-wrapper">
          <table className="cip-table">
            <thead>
              <tr>
                {selectedColumns.includes("Origin") && (
                  <th
                    style={{
                      ...headerCellStyle(),
                      textAlign: "center",
                      minWidth: 160,
                      fontWeight: 700,
                    }}
                  >
                    {t("Origin")}
                  </th>
                )}

                {selectedColumns.includes("Destination") && (
                  <th
                    style={{
                      ...headerCellStyle(),
                      textAlign: "center",
                      minWidth: 160,
                      fontWeight: 700,
                    }}
                  >
                    {t("Destination")}
                  </th>
                )}

                {selectedColumns.includes("BidValue") && (
                  <th
                    className="cip-col-bid"
                    style={{
                      ...headerCellStyle(),
                      fontWeight: 700,
                    }}
                  >
                    {t("BidValue")}
                  </th>
                )}

                {selectedColumns.includes("Loads") && (
                  <th
                    className="cip-col-loads"
                    style={{
                      ...headerCellStyle(),
                      fontWeight: 700,
                    }}
                  >
                    {t("Loads")}
                  </th>
                )}

                {selectedColumns.includes("EstimatedTransitDays") && (
                  <th
                    className="cip-col-etd"
                    style={{
                      ...headerCellStyle(),
                      fontWeight: 700,
                    }}
                  >
                    {t("EstimatedTransitDays")}
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {pagedRows.length === 0 && (
                <tr>
                  <td
                    colSpan={selectedColumns.length || 1}
                    className="cip-empty-row"
                    style={cellStyle()}
                  >
                    {t("No data available")}
                  </td>
                </tr>
              )}

              {pagedRows.map((lane) => {
                const laneId = lane.laneId;
                const laneDraft = draft[laneId] || {};
                const bidValue = laneDraft.bidValue ?? "";
                const loadsValue = laneDraft.loads ?? "";
                const etdValue = laneDraft.estimatedTransitDays ?? "";
                const disabled = isDisabledCell(laneId);

                const maxForLane = Number(maxLoadsByLane?.[laneId] ?? 0);
                const exceedsLoads = !!loadsExceedByLane?.[laneId];

                const numericCellClass =
                  !readOnly && !isViewMode && laneId
                    ? "cip-cell cip-cell-clickable"
                    : "cip-cell";

                return (
                  <tr key={laneId}>
                    {selectedColumns.includes("Origin") && (
                      <td
                        style={{
                          ...subHeaderCellStyle(),
                          fontWeight: 700,
                        }}
                      >
                        {lane.origin}
                      </td>
                    )}

                    {selectedColumns.includes("Destination") && (
                      <td
                        style={{
                          ...subHeaderCellStyle(),
                          fontWeight: 700,
                        }}
                      >
                        {lane.destination}
                      </td>
                    )}

                    {selectedColumns.includes("BidValue") && (
                      <td
                        className={`${numericCellClass} cip-col-bid`}
                        style={cellStyle()}
                        onClick={() => activateLane(laneId)}
                      >
                        {laneId ? (
                          readOnly || isViewMode ? (
                            <ViewCell value={bidValue} suffix={currencyLabel} />
                          ) : isEditMode && activeLaneId !== laneId ? (
                            <ViewCell value={bidValue} suffix={currencyLabel} />
                          ) : (
                            renderBidInput({
                              laneId,
                              value: bidValue,
                              disabled,
                            })
                          )
                        ) : null}
                      </td>
                    )}

                    {selectedColumns.includes("Loads") && (
                      <td
                        className={`${numericCellClass} cip-col-loads`}
                        style={cellStyle()}
                        onClick={() => activateLane(laneId)}
                      >
                        {laneId ? (
                          readOnly || isViewMode ? (
                            <ViewCell value={loadsValue} />
                          ) : isEditMode && activeLaneId !== laneId ? (
                            <ViewCell value={loadsValue} />
                          ) : (
                            <div
                              className={
                                exceedsLoads
                                  ? "cip-input-exceeds"
                                  : "cip-input-stack"
                              }
                              title={
                                exceedsLoads
                                  ? `${t("LoadsExceedRemainingCapacity")} (${t("Max") || "Max"
                                  }: ${maxForLane})`
                                  : ""
                              }
                              onKeyDownCapture={handleSubmitShortcut}
                            >
                              <MInputTextField
                                type="number"
                                label=""
                                value={loadsValue}
                                placeholder={`${t("Max") || "Max"
                                  }: ${maxForLane}`}
                                onChange={(e) =>
                                  onChangeLoads(laneId, e.target.value)
                                }
                                onKeyDown={blockNonIntegerKeys}
                                disabled={disabled}
                                placeholderColor="var(--color-red-light)"
                              />
                            </div>
                          )
                        ) : null}
                      </td>
                    )}

                    {selectedColumns.includes("EstimatedTransitDays") && (
                      <td
                        className={`${numericCellClass} cip-col-etd`}
                        style={cellStyle()}
                        onClick={() => activateLane(laneId)}
                      >
                        {laneId ? (
                          readOnly || isViewMode ? (
                            <ViewCell value={etdValue} />
                          ) : isEditMode && activeLaneId !== laneId ? (
                            <ViewCell value={etdValue} />
                          ) : (
                            <div
                              className="cip-input-stack"
                              onKeyDownCapture={handleSubmitShortcut}
                            >
                              <MInputTextField
                                type="number"
                                label=""
                                value={etdValue}
                                onChange={(e) =>
                                  onChangeEstimatedTransitDays(
                                    laneId,
                                    e.target.value
                                  )
                                }
                                onKeyDown={blockNonIntegerKeys}
                                disabled={disabled}
                              />
                            </div>
                          )
                        ) : null}

                        {!readOnly &&
                          isCreateMode &&
                          laneId &&
                          (status[laneId]?.ok || status[laneId]?.error) && (
                            <div
                              className={`cip-status ${status[laneId]?.ok
                                ? "cip-status-ok"
                                : "cip-status-error"
                                }`}
                            >
                              {status[laneId]?.ok
                                ? t("Submitted")
                                : status[laneId].error}
                            </div>
                          )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="cip-paginator-wrapper">
          <Paginator
            first={first}
            rows={rowsPerPage}
            totalRecords={filteredRows.length}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={(event) => {
              setFirst(event.first);
              setRowsPerPage(event.rows);
            }}
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          />
        </div>
      </div>
    </div>
  );

  //endregion
}

//endregion

//region PropTypes

InviteTableSection.propTypes = {
  token: PropTypes.string,
  mode: PropTypes.oneOf(["spot", "phase"]),
  lanes: PropTypes.array,
  crudMode: PropTypes.oneOf(["create", "edit", "view"]).isRequired,
  readOnly: PropTypes.bool,
  prefillQuotes: PropTypes.array,
  submitServices: PropTypes.object,
  editServices: PropTypes.object,
  tenderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tenderPhaseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  carrierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tenderLoads: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  acceptedLoadsByLane: PropTypes.object,
  currencySymbol: PropTypes.string,
  onRefresh: PropTypes.func,
  onBatchSubmitSuccess: PropTypes.func,
};

//endregion